import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'F1-1'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '📈',
    category: '정비례 관계식',
    learnStepId: 'FA01',
    question: 'x=4일 때 y=12이고 y가 x에 정비례할 때, y를 x의 식으로 나타내시오.',
    correctAnswer: 'a=12÷4=3, y=3x',
    wrongAnswer: 'y=12-4=8, y=x+8',
    misconceptionCode: 'FP01',
    note: '정비례(y=ax)와 일차함수(y=ax+b)를 혼동함',
    analysis: {
      diagnosis: '학생이 "y=x+8"이라고 답했습니다. 정비례를 "y=ax+b" 형태로 잘못 이해했습니다.',
      rootCause: '정비례는 반드시 y=ax 형태(원점 통과)이며, y/x=a가 항상 일정해야 합니다. y=ax+b(b≠0)는 정비례가 아닙니다.',
      correction: '올바른 풀이:\n① 정비례 관계: y = ax\n② 비례상수 a = y÷x = 12÷4 = 3\n③ 따라서 y = 3x\n검산: x=4 → y=3×4=12 ✓',
      example: 'x=2일 때 y=10이고 정비례하면?\na=10÷2=5, y=5x\nx=6: y=5×6=30',
    },
    followUps: [
      { q: 'y=3x에서 x=7이면 y는?', a: 'y=3×7=21' },
      { q: 'y가 x에 정비례하고 x=5,y=20이면 비례상수는?', a: 'a=20÷5=4' },
      { q: 'y=2x+1은 정비례인가?', a: '아니오. b=1≠0이므로 정비례 아님' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '📉',
    category: '정비례 그래프 사분면',
    learnStepId: 'FA02',
    question: 'y=-2x의 그래프는 어느 사분면을 지나는가?',
    correctAnswer: '2사분면과 4사분면',
    wrongAnswer: '1사분면과 3사분면',
    misconceptionCode: 'FP01',
    note: 'a<0일 때 그래프 위치(사분면)를 a>0과 혼동함',
    analysis: {
      diagnosis: '학생이 "1사분면과 3사분면"이라고 답했습니다. a>0일 때의 그래프 위치를 a<0에 적용했습니다.',
      rootCause: 'y=ax에서 a>0이면 오른쪽 위(↗) 방향으로 1·3사분면을, a<0이면 오른쪽 아래(↘) 방향으로 2·4사분면을 지납니다.',
      correction: '올바른 풀이:\n① y=-2x: a=-2<0\n② a<0 → 오른쪽 아래 방향\n③ 2사분면(x<0, y>0)·4사분면(x>0, y<0) 통과\n예: x=1→y=-2 (4사분면), x=-1→y=2 (2사분면)',
      example: 'y=3x: a=3>0 → 1·3사분면\ny=-½x: a=-½<0 → 2·4사분면',
    },
    followUps: [
      { q: 'y=4x는 어느 사분면을 지나는가?', a: '1사분면과 3사분면 (a=4>0)' },
      { q: 'y=-x의 그래프 방향은?', a: '오른쪽 아래(↘), 2·4사분면' },
      { q: '|a|가 클수록 그래프는 어느 축에 가까워지는가?', a: 'y축에 가까워짐 (가파름)' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '🔄',
    category: '반비례 관계식',
    learnStepId: 'FA03',
    question: 'x=3, y=8일 때 y가 x에 반비례한다. y를 x의 식으로 나타내시오.',
    correctAnswer: 'a=xy=3×8=24, y=24/x',
    wrongAnswer: 'a=y÷x=8÷3, y=(8/3)x',
    misconceptionCode: 'FP02',
    note: '반비례(y=a/x)를 정비례(y=ax)로 혼동하여 비례상수를 y÷x로 구함',
    analysis: {
      diagnosis: '학생이 정비례처럼 a=y÷x로 계산했습니다. 반비례의 비례상수는 a=xy입니다.',
      rootCause: '정비례(y=ax): a=y÷x | 반비례(y=a/x): a=xy. 두 공식을 혼동하지 않아야 합니다.',
      correction: '올바른 풀이:\n① 반비례: y = a/x → xy = a\n② a = x×y = 3×8 = 24\n③ y = 24/x\n검산: x=3 → y=24/3=8 ✓',
      example: 'x=6, y=5이고 반비례하면?\na=6×5=30, y=30/x\nx=10: y=30/10=3',
    },
    followUps: [
      { q: 'y=24/x에서 x=8이면 y는?', a: 'y=24/8=3' },
      { q: 'xy=18인 반비례 관계식은?', a: 'y=18/x' },
      { q: 'x=4, y=9일 때 반비례하면 비례상수는?', a: 'a=4×9=36' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '∞',
    category: '반비례 그래프',
    learnStepId: 'FA04',
    question: 'y=12/x의 그래프에 대해 옳은 것을 고르시오.\n① 원점을 지나는 직선이다.\n② 1사분면과 3사분면에 있다.\n③ x축과 한 점에서 만난다.',
    correctAnswer: '② 1사분면과 3사분면에 있다.',
    wrongAnswer: '① 원점을 지나는 직선이다.',
    misconceptionCode: 'FP02',
    note: '반비례 그래프(쌍곡선)를 정비례 그래프(직선)로 혼동함',
    analysis: {
      diagnosis: '학생이 "원점을 지나는 직선"이라고 답했습니다. 정비례와 반비례 그래프의 모양을 혼동했습니다.',
      rootCause: '정비례(y=ax): 원점을 지나는 직선 | 반비례(y=a/x): 쌍곡선, 원점 대칭, 축과 교점 없음',
      correction: '올바른 분석:\n① ✗ 반비례는 직선이 아니라 쌍곡선\n② ✓ a=12>0이므로 1사분면·3사분면\n③ ✗ 축에 한없이 가까워지지만 닿지 않음',
      example: 'y=6/x (쌍곡선, a>0: 1·3사분면)\ny=-6/x (쌍곡선, a<0: 2·4사분면)',
    },
    followUps: [
      { q: 'y=-8/x의 그래프는 몇 사분면에 있는가?', a: '2사분면과 4사분면 (a=-8<0)' },
      { q: '반비례 그래프의 대칭 중심은?', a: '원점 (0, 0)' },
      { q: 'y=a/x에서 |a|가 커지면 그래프는?', a: '원점에서 멀어짐 (바깥쪽으로 이동)' },
    ],
  },
]

// ── 오개념 코드 설명 ──────────────────────────────────────────────────────────
const MISCONCEPTION_INFO: Record<string, { label: string; color: string; tip: string }> = {
  FP01: { label: '정비례 개념 혼동', color: 'bg-blue-100 text-blue-700 border-blue-200', tip: 'y=ax(정비례)와 y=ax+b(일차함수)를 혼동하지 마세요. 정비례는 반드시 원점을 통과!' },
  FP02: { label: '반비례 개념 혼동', color: 'bg-violet-100 text-violet-700 border-violet-200', tip: '반비례 a=xy, 정비례 a=y/x. 둘을 헷갈리지 않도록 표 검증 습관을 만드세요!' },
}

// ── 서브 컴포넌트: 시나리오 카드 ─────────────────────────────────────────────
function ScenarioCard({ scenario }: { scenario: TutorScenario }) {
  const [phase, setPhase] = useState<'intro' | 'wrong' | 'analysis' | 'followup'>('intro')
  const [fqIdx, setFqIdx] = useState(0)
  const [fqInput, setFqInput] = useState('')
  const [fqResult, setFqResult] = useState<boolean | null>(null)
  const [showFqAnswer, setShowFqAnswer] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const mc = MISCONCEPTION_INFO[scenario.misconceptionCode]

  const checkFollowUp = () => {
    const correct = scenario.followUps[fqIdx].a.toLowerCase()
    const input = fqInput.trim().toLowerCase()
    setFqResult(correct.includes(input.replace(/\s/g, '')) || input.length > 0)
    setShowFqAnswer(true)
  }

  const nextFollowUp = () => {
    if (fqIdx < scenario.followUps.length - 1) {
      setFqIdx(fqIdx + 1)
      setFqInput('')
      setFqResult(null)
      setShowFqAnswer(false)
    }
  }

  return (
    <div className="card border border-slate-200">
      {/* 헤더 */}
      <button
        className="w-full flex items-center gap-3 text-left"
        onClick={() => setExpanded(!expanded)}>
        <span className="text-2xl">{scenario.emoji}</span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-black text-slate-800">{scenario.label}</span>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{scenario.category}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{scenario.question.slice(0, 40)}...</p>
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {/* 오개념 배지 */}
          {mc && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${mc.color}`}>
              <AlertTriangle size={12} />
              <span className="text-xs font-bold">{mc.label} ({scenario.misconceptionCode})</span>
            </div>
          )}

          {/* 문제 */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">
              <Target size={11} /> 문제
            </p>
            <p className="text-sm text-slate-800 font-medium whitespace-pre-line">{scenario.question}</p>
          </div>

          {/* 단계 */}
          {phase === 'intro' && (
            <button
              onClick={() => setPhase('wrong')}
              className="w-full py-3 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
              <XCircle size={15} /> 학생 오답 확인하기
            </button>
          )}

          {(phase === 'wrong' || phase === 'analysis' || phase === 'followup') && (
            <div className="space-y-3">
              {/* 오답 */}
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                <p className="text-xs font-bold text-rose-600 mb-1 flex items-center gap-1">
                  <XCircle size={11} /> 학생 오답
                </p>
                <p className="text-sm font-mono text-rose-800">{scenario.wrongAnswer}</p>
                <p className="text-xs text-rose-500 mt-1 italic">{scenario.note}</p>
              </div>

              {phase === 'wrong' && (
                <button
                  onClick={() => setPhase('analysis')}
                  className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                  <Brain size={15} /> AI 튜터 분석 보기
                </button>
              )}
            </div>
          )}

          {(phase === 'analysis' || phase === 'followup') && (
            <div className="space-y-3">
              {/* 분석 */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs font-bold text-blue-600 mb-1 flex items-center gap-1">
                    <MessageCircle size={11} /> 진단
                  </p>
                  <p className="text-sm text-slate-700">{scenario.analysis.diagnosis}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-blue-600 mb-1 flex items-center gap-1">
                    <AlertTriangle size={11} /> 근본 원인
                  </p>
                  <p className="text-sm text-slate-700">{scenario.analysis.rootCause}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                    <CheckCircle size={11} /> 올바른 풀이
                  </p>
                  <p className="text-sm font-mono text-emerald-800 whitespace-pre-line bg-emerald-50 rounded-lg p-3">
                    {scenario.analysis.correction}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-violet-600 mb-1 flex items-center gap-1">
                    <BookOpen size={11} /> 유사 예제
                  </p>
                  <p className="text-sm font-mono text-violet-800 whitespace-pre-line bg-violet-50 rounded-lg p-3">
                    {scenario.analysis.example}
                  </p>
                </div>
              </div>

              {/* 오개념 팁 */}
              {mc && (
                <div className={`rounded-xl p-3 border ${mc.color}`}>
                  <p className="text-xs font-bold mb-1">💡 오개념 방지 팁</p>
                  <p className="text-xs">{mc.tip}</p>
                </div>
              )}

              {phase === 'analysis' && (
                <button
                  onClick={() => setPhase('followup')}
                  className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors">
                  <Sparkles size={15} /> 확인 문제 풀기
                </button>
              )}
            </div>
          )}

          {phase === 'followup' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-700 mb-3 flex items-center gap-1">
                <Target size={11} /> 확인 문제 {fqIdx + 1}/{scenario.followUps.length}
              </p>
              <p className="text-sm font-medium text-slate-800 mb-3">{scenario.followUps[fqIdx].q}</p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={fqInput}
                  onChange={e => setFqInput(e.target.value)}
                  placeholder="답을 입력하세요"
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-emerald-400"
                />
                <button onClick={checkFollowUp}
                  className="px-4 py-2 bg-emerald-500 text-white text-xs font-bold rounded-lg hover:bg-emerald-600">
                  확인
                </button>
              </div>
              {showFqAnswer && (
                <div className="space-y-2">
                  <div className="bg-white border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs font-bold text-emerald-600 mb-1">정답</p>
                    <p className="text-sm font-mono text-emerald-800">{scenario.followUps[fqIdx].a}</p>
                  </div>
                  {fqIdx < scenario.followUps.length - 1 ? (
                    <button onClick={nextFollowUp}
                      className="w-full py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold rounded-lg text-xs flex items-center justify-center gap-1 transition-colors">
                      다음 문제 <ChevronRight size={12} />
                    </button>
                  ) : (
                    <div className="text-center py-2">
                      <span className="text-sm font-bold text-emerald-600">✅ 모든 확인 문제 완료!</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 리셋 */}
          {phase !== 'intro' && (
            <button
              onClick={() => { setPhase('intro'); setFqIdx(0); setFqInput(''); setFqResult(null); setShowFqAnswer(false) }}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
              <RotateCcw size={11} /> 처음부터
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function F1_1TutorPage() {
  const unitProgress = useUnitProgress(UNIT_ID)
  const fragments = computePredictedFragments(unitProgress, 3)

  return (
    <div className="p-4 space-y-4">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-blue-500 to-violet-600 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">🤖</div>
          <div>
            <h2 className="text-xl font-black">AI 튜터 · 정비례와 반비례</h2>
            <p className="text-sm text-blue-100">F1-1 · 오개념 진단 & 교정</p>
          </div>
        </div>
        <p className="text-xs text-blue-100 leading-relaxed">
          정비례·반비례에서 자주 틀리는 오개념을 AI 튜터가<br />
          단계별로 진단하고 올바른 개념으로 안내합니다.
        </p>
      </div>

      {/* AI 예상 취약 개념 */}
      {fragments.length > 0 && (
        <div className="card border border-amber-200 bg-amber-50">
          <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
            <Brain size={12} /> AI 예상 취약 개념
          </p>
          <div className="space-y-1.5">
            {fragments.slice(0, 2).map(f => {
              const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
              return (
                <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
                  <span>{f.fragment.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate">{f.fragment.title}</p>
                    <p className="text-xs text-slate-500 truncate">{f.fragment.fixTip.slice(0, 40)}...</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오개념 안내 */}
      <div className="card bg-slate-50 border border-slate-200">
        <p className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1">
          <AlertTriangle size={12} /> F1-1 주요 오개념
        </p>
        <div className="space-y-2">
          {Object.entries(MISCONCEPTION_INFO).map(([code, info]) => (
            <div key={code} className={`flex items-start gap-2 px-3 py-2 rounded-xl border ${info.color}`}>
              <span className="text-xs font-black w-12 flex-shrink-0">{code}</span>
              <div>
                <p className="text-xs font-bold">{info.label}</p>
                <p className="text-xs mt-0.5">{info.tip}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 시나리오 목록 */}
      <div className="space-y-3">
        <p className="text-sm font-black text-slate-700 flex items-center gap-2">
          <MessageCircle size={14} /> 오개념 시나리오
        </p>
        {DEMO_SCENARIOS.map(scenario => (
          <ScenarioCard key={scenario.id} scenario={scenario} />
        ))}
      </div>
    </div>
  )
}
